import type {
  IShopifyPageAssigned,
  IShopifyPageAssignedResponse,
  ShopifyAsset,
} from '~/modules/shopify/types';

import { TemplateType } from '~/types/template';
import { deepCopy } from './../utils/deepCopy';
import { create } from 'zustand';
export type IAssigned = Partial<Record<TemplateType, IShopifyPageAssignedResponse>>;
export type IUnassigned = Partial<Record<TemplateType, IShopifyPageAssigned>>;
export type IAssignedLoading = Partial<Record<TemplateType, boolean>>;
interface AssignedStore {
  assigned?: IAssigned;
  unassigned?: IUnassigned;
  assetsWithAssigned: ShopifyAsset[];
  clearCache: boolean;
  /** Types whose next fetch must bypass the server cache, consumed once fetched. */
  clearCacheTypes: TemplateType[];
  loading: IAssignedLoading;
  setLoadingByType: (type: TemplateType, loading: boolean) => void;
  setAssigned: (assigned: IAssigned) => void;
  setAssetsWithAssigned: (assetsWithAssigned: ShopifyAsset[]) => void;
  setClearCache: (clearCache: boolean) => void;
  addClearCacheType: (type: TemplateType) => void;
  removeClearCacheType: (type: TemplateType) => void;
  getAssigned: () => IAssigned | undefined;
  getAssignedByType: (type: TemplateType) => IShopifyPageAssignedResponse | undefined;
  getUnassignedByType: (type: TemplateType) => IShopifyPageAssigned | undefined;
  setAssignedByType: (type: TemplateType, assigned?: IShopifyPageAssignedResponse) => void;
  setUnassignedByType: (type: TemplateType, unassigned?: IShopifyPageAssigned) => void;
  resetAssigned: () => void;
}

export const useAssignedStore = create<AssignedStore>()((set, get) => ({
  assigned: {},
  unassigned: {},
  assetsWithAssigned: [],
  clearCache: false,
  clearCacheTypes: [],
  loading: {
    [TemplateType.PRODUCT]: true,
    [TemplateType.COLLECTION]: true,
    [TemplateType.PAGE]: true,
    [TemplateType.ARTICLE]: true,
  },
  setAssetsWithAssigned: (assetsWithAssigned) => set({ assetsWithAssigned }),
  setAssigned: (assigned) => set({ assigned }),
  resetAssigned: () => set({ assigned: {}, unassigned: {} }),
  setClearCache: (clearCache) => set({ clearCache }),
  addClearCacheType: (type) =>
    set((state) =>
      state.clearCacheTypes.includes(type)
        ? state
        : { clearCacheTypes: [...state.clearCacheTypes, type] },
    ),
  removeClearCacheType: (type) =>
    set((state) =>
      state.clearCacheTypes.includes(type)
        ? { clearCacheTypes: state.clearCacheTypes.filter((item) => item !== type) }
        : state,
    ),
  getAssigned: () => get().assigned,
  getAssignedByType: (type) => get().assigned?.[type],
  getUnassignedByType: (type) => get().unassigned?.[type],
  setAssignedByType: (type, value) => {
    const currentAssigned = deepCopy(get().assigned);
    let dataAssigned;

    if (currentAssigned) {
      dataAssigned = { ...currentAssigned, [type]: value };
    } else {
      dataAssigned = { [type]: value };
    }

    return set({ assigned: dataAssigned });
  },
  setLoadingByType: (type, value) => {
    const currentLoading = deepCopy(get().loading);
    let loadingUpdated;

    if (currentLoading) {
      loadingUpdated = { ...currentLoading, [type]: value };
    } else {
      loadingUpdated = { [type]: value };
    }

    return set({ loading: loadingUpdated });
  },
  setUnassignedByType: (type, value) => {
    const unassigned = JSON.parse(JSON.stringify({ ...get().unassigned, [type]: value }));
    return set({ unassigned });
  },
}));

export default useAssignedStore;
